/**
 * ListWidgetsPanel — zakładka „Widgety" w Kreatorze Listy (osobny widok).
 *
 * Edytor pulpitu KPI nad listą (wzorzec SellCall `WidgetConfig`). Pełny picker
 * 14 typów kart pogrupowany w kategorie (Podstawowe / Wykresy / Zaawansowane).
 * Funkcjonalne dziś (liczone przez backend `/stats`):
 *   - `counter`   — liczba rekordów spełniających filtr,
 *   - `aggregate` — agregacja (suma/średnia/min/max/liczba unikalnych) na polu.
 * Pozostałe typy są widoczne w pickerze z plakietką „Wkrótce" (zablokowane do
 * wyboru) — roadmapa pulpitu, schema (`list-view-widgets.ts`) zna już wszystkie.
 *
 * Karta edytowana jest w SZUFLADZIE z prawej (Drawer) — jeden panel, dwa kroki:
 * najpierw wybór typu (siatka 14 kart w kategoriach, „Wkrótce" zablokowane), po
 * wyborze płynne przejście do formularza (etykieta + kolor akcentu + dla agregatu
 * funkcja/pole + warunki filtra AND + WŁASNY wygląd nadpisujący domyślny).
 * „Zmień" w formularzu wraca do kroku wyboru typu. Edycja istniejącej karty
 * otwiera szufladę od razu na formularzu. Lista kart = drag&drop reorder
 * (@dnd-kit, parytet z „Kolumny").
 *
 * Domyślny wygląd kart (tło/gradient/szkło/kolory/zaokrąglenie/cień) edytowany
 * jest tutaj (sekcja „Domyślny wygląd kart") — przeniesiony z zakładki „Strona".
 * Zapisywany do `pageConfig.widgetStyle`; pojedyncza karta nadpisuje go swoim
 * `style` (runtime paska stosuje `widget.style ?? widgetStyle`).
 *
 * Zapis przez `useTableLayout(slug)`: PUT całego `list_config` z nowymi
 * `widgets` ORAZ `pageConfig.widgetStyle` (reszta `pageConfig` — tytuł/opis/
 * przełączniki/styl paska filtrów — zachowana bez zmian).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  type AggregateFunction,
  defaultPageConfig,
  type FilterConfig,
  type FilterOperator,
  type RoundingScale,
  type ShadowScale,
  type WidgetConfig,
  type WidgetStyle,
  type WidgetType,
} from '@saas/shared';
import { ArrowLeft, GripVertical, Pencil, Plus, Save, Trash2 } from 'lucide-react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Drawer } from '@/components/Drawer';
import { Dropdown } from '@/components/Dropdown';
import { useToast } from '@/contexts/ToastContext';

import { useColumnLabel } from './list-column-registry';
import { useTableLayout } from './useTableLayout';

/**
 * Wszystkie 14 typów kart w kategoriach pickera. Kolejność = kolejność wyświetlania.
 */
const WIDGET_CATEGORIES: Array<{ key: 'basic' | 'charts' | 'advanced'; types: WidgetType[] }> = [
  { key: 'basic', types: ['counter', 'aggregate', 'progress', 'kpi', 'stat_card'] },
  {
    key: 'charts',
    types: ['pie', 'bar', 'donut', 'trend', 'sparkline', 'heatmap', 'funnel', 'gauge'],
  },
  { key: 'advanced', types: ['table'] },
];

/** Typy realnie liczone przez backend dzisiaj. Reszta = „Wkrótce" (zablokowane). */
const AVAILABLE_TYPES = new Set<WidgetType>(['counter', 'aggregate']);

/** Funkcje agregujące liczone SQL-em. */
const AGG_FUNCTIONS: AggregateFunction[] = ['sum', 'avg', 'min', 'max', 'count_distinct'];

/**
 * Operatory warunków filtra karty. Bez `in` — backend filtrów nagłówka go nie
 * obsługuje (zostawienie go dawało karty, których filtr cicho nie działał).
 */
const OPERATORS: FilterOperator[] = [
  'eq',
  'neq',
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
  'gt',
  'gte',
  'lt',
  'lte',
  'in',
  'is_null',
  'is_not_null',
];

const NO_VALUE_OPS = new Set<FilterOperator>(['is_null', 'is_not_null']);

/** Lista wartości „jest jednym z" — rozbij tekst po przecinku na tablicę. */
const parseInList = (text: string): string[] =>
  text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

const DEFAULT_COLOR = '#2563eb';

// Skala zaokrąglenia → px (podgląd na żywo; runtime mapuje analogicznie).
const ROUNDING_PX: Record<RoundingScale, number> = {
  none: 0,
  sm: 4,
  md: 6,
  lg: 8,
  xl: 12,
  '2xl': 16,
};

// Skala cienia → CSS box-shadow (podgląd).
const SHADOW_CSS: Record<ShadowScale, string> = {
  none: 'none',
  sm: '0 1px 2px 0 rgba(0,0,0,0.06)',
  md: '0 4px 6px -1px rgba(0,0,0,0.12)',
  lg: '0 10px 15px -3px rgba(0,0,0,0.14)',
  xl: '0 20px 25px -5px rgba(0,0,0,0.16)',
};

const ROUNDING_OPTIONS: RoundingScale[] = ['none', 'sm', 'md', 'lg', 'xl', '2xl'];
const SHADOW_OPTIONS: ShadowScale[] = ['none', 'sm', 'md', 'lg', 'xl'];

interface FieldOption {
  id: string;
  label: string;
}

/**
 * Stan szuflady edycji widgetu. `picker` = krok wyboru typu (dodawanie nowej
 * karty `add` albo zmiana typu istniejącej `change`), `edit` = formularz karty.
 */
type DrawerState =
  | { step: 'picker'; mode: 'add' }
  | { step: 'picker'; mode: 'change'; id: string }
  | { step: 'edit'; id: string };

/** Czy obiekt stylu ma jakiekolwiek znaczące pole (do prune'owania przed zapisem). */
function hasStyle(style: WidgetStyle | undefined): boolean {
  if (!style) return false;
  return Object.values(style).some((v) => v != null);
}

/** Karta widgetu — efektywny styl inline (per-widget `style` lub domyślny). */
function cardStyleFrom(style: WidgetStyle): React.CSSProperties {
  return {
    background: style.backgroundGradient
      ? `linear-gradient(135deg, ${style.backgroundGradient.from}, ${style.backgroundGradient.to})`
      : (style.backgroundColor ?? (style.glassEffect ? 'rgba(255,255,255,0.08)' : undefined)),
    color: style.textColor,
    borderRadius: style.rounding != null ? ROUNDING_PX[style.rounding] : 8,
    boxShadow: style.shadow != null ? SHADOW_CSS[style.shadow] : undefined,
    backdropFilter: style.glassEffect ? 'blur(8px)' : undefined,
    WebkitBackdropFilter: style.glassEffect ? 'blur(8px)' : undefined,
  };
}

export function ListWidgetsPanel({
  slug,
  hideAddWidget = false,
}: {
  slug: string;
  /**
   * HIDDEN (tymczasowo): ukrywa przycisk „+ Dodaj widget" (zakładka „Widgety" buildera
   * widoku listy pozycji menu). Reszta zakładki (lista istniejących kart, sekcja
   * „Domyślny wygląd kart", podgląd) działa bez zmian.
   * Powód: dodawanie widgetów tutaj nie ma jeszcze sensu — skonfigurowane widgety
   * / nazwane widoki menu nie renderują się u klienta (materializeEntityLayouts bierze
   * tylko isDefault; render multi-view u klienta = TODO „krok D"). Odblokować, gdy
   * render nazwanych widoków u tenanta będzie gotowy.
   * Domyślnie false — standalone builder + kreator encji-odwołania nie są dotknięte.
   */
  hideAddWidget?: boolean;
}): React.ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const { layout, isLoading, saveLayout, isSaving } = useTableLayout(slug);
  // Hub Krok 2: provider etykiet świadomy trybu `template` (pola szablonu po nazwie).
  const columnLabel = useColumnLabel(slug);

  const [widgets, setWidgets] = useState<WidgetConfig[] | null>(null);
  const [defaultStyle, setDefaultStyle] = useState<WidgetStyle>({});
  const [pendingDelete, setPendingDelete] = useState<WidgetConfig | null>(null);
  // Szuflada edycji — jeden panel, dwa kroki:
  //  - `picker` (mode add | change): siatka wyboru typu,
  //  - `edit`: formularz pojedynczej karty.
  const [drawer, setDrawer] = useState<DrawerState | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    if (layout?.layoutConfig) {
      setWidgets([...(layout.layoutConfig.widgets ?? [])].sort((a, b) => a.order - b.order));
      setDefaultStyle(layout.layoutConfig.pageConfig?.widgetStyle ?? {});
    }
  }, [layout]);

  if (isLoading) return <p className="text-sm text-text-tertiary">…</p>;
  if (!layout || !widgets) {
    return <p className="text-sm text-text-muted">{t('tenant.listBuilder.columns.noLayout')}</p>;
  }

  const fieldOptions: FieldOption[] = (layout.layoutConfig.columns ?? []).map((c) => ({
    id: c.id,
    label: columnLabel(c.id) ?? c.id,
  }));

  const editingId = drawer?.step === 'edit' ? drawer.id : null;
  const editing = editingId ? (widgets.find((w) => w.id === editingId) ?? null) : null;

  const update = (id: string, patch: Partial<WidgetConfig>) =>
    setWidgets((ws) => ws!.map((w) => (w.id === id ? { ...w, ...patch } : w)));

  const createWidget = (type: WidgetType) => {
    const id = crypto.randomUUID();
    setWidgets((ws) => [
      ...ws!,
      {
        id,
        label: '',
        color: DEFAULT_COLOR,
        type,
        clickAction: 'filter',
        order: ws!.length,
      },
    ]);
    return id;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setWidgets((ws) => {
        if (!ws) return ws;
        const oldIndex = ws.findIndex((w) => w.id === active.id);
        const newIndex = ws.findIndex((w) => w.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return ws;
        return arrayMove(ws, oldIndex, newIndex);
      });
    }
  };

  const removeWidget = (id: string) =>
    setWidgets((ws) => ws!.filter((w) => w.id !== id).map((w, i) => ({ ...w, order: i })));

  // Wybór typu w kroku `picker` → przejście do kroku `edit` w tej samej szufladzie.
  const handlePick = (type: WidgetType) => {
    if (!drawer || drawer.step !== 'picker') return;
    if (drawer.mode === 'add') {
      const id = createWidget(type);
      setDrawer({ step: 'edit', id });
    } else {
      update(drawer.id, { type });
      setDrawer({ step: 'edit', id: drawer.id });
    }
  };

  const handleSave = async () => {
    // Pomijamy karty bez etykiety; przenumerowanie order; prune pustego stylu.
    const cleaned: WidgetConfig[] = widgets
      .filter((w) => w.label.trim().length > 0)
      .map((w, i) => ({
        ...w,
        label: w.label.trim(),
        order: i,
        style: hasStyle(w.style) ? w.style : undefined,
      }));
    const prevPage = layout.layoutConfig.pageConfig ?? defaultPageConfig;
    const nextPage = {
      ...prevPage,
      widgetStyle: hasStyle(defaultStyle) ? defaultStyle : undefined,
    };
    await saveLayout({ ...layout.layoutConfig, widgets: cleaned, pageConfig: nextPage });
    toast.show({ variant: 'success', title: t('tenant.listBuilder.widgets.saved') });
  };

  return (
    <div className="space-y-5" data-testid="list-widgets-panel">
      <p className="text-sm text-text-tertiary">{t('tenant.listBuilder.widgets.intro')}</p>

      {/* ── Lista kart ── */}
      {widgets.length === 0 && (
        <p className="text-sm text-text-muted">{t('tenant.listBuilder.widgets.empty')}</p>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={widgets.map((w) => w.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {widgets.map((widget) => (
              <SortableWidgetCard
                key={widget.id}
                widget={widget}
                onEdit={() => setDrawer({ step: 'edit', id: widget.id })}
                onDelete={() => setPendingDelete(widget)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* HIDDEN (tymczasowo): „+ Dodaj widget" ukrywany w builderze widoku listy pozycji
          menu (hideAddWidget) — dodawanie widgetów tu nie ma jeszcze sensu, bo nazwane widoki
          menu nie renderują się u klienta (krok D nadal TODO). Reszta zakładki bez zmian. */}
      {!hideAddWidget && (
        <button
          type="button"
          onClick={() => setDrawer({ step: 'picker', mode: 'add' })}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border-secondary px-3 py-1.5 text-sm text-text-primary hover:bg-bg-secondary"
          data-testid="list-widgets-add"
        >
          <Plus size={14} aria-hidden="true" />
          {t('tenant.listBuilder.widgets.add')}
        </button>
      )}

      {/* ── Domyślny wygląd kart (przeniesione z zakładki „Strona") ── */}
      <section
        className="space-y-4 rounded-lg border border-border-secondary p-4"
        data-testid="list-widgets-default-style"
      >
        <div>
          <h3 className="text-sm font-semibold text-text-primary">
            {t('tenant.listBuilder.widgets.defaultStyle.title')}
          </h3>
          <p className="text-xs text-text-muted">
            {t('tenant.listBuilder.widgets.defaultStyle.hint')}
          </p>
        </div>
        <WidgetStyleEditor
          value={defaultStyle}
          onChange={setDefaultStyle}
          testId="list-widgets-default"
        />
        <WidgetStylePreview style={defaultStyle} />
      </section>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="inline-flex items-center gap-2 rounded-lg bg-accent-600 px-4 py-1.5 text-sm text-white hover:bg-accent-700 disabled:opacity-50"
          data-testid="list-widgets-save"
        >
          <Save size={16} aria-hidden="true" />
          {isSaving
            ? t('tenant.listBuilder.widgets.saving')
            : t('tenant.listBuilder.widgets.saveBtn')}
        </button>
      </div>

      {/* ── Szuflada edycji (wybór typu → formularz, jeden panel) ── */}
      {drawer && (
        <WidgetDrawer
          state={drawer}
          editing={editing}
          fieldOptions={fieldOptions}
          defaultStyle={defaultStyle}
          onPick={handlePick}
          onUpdate={(patch) => editing && update(editing.id, patch)}
          onChangeType={() =>
            editing && setDrawer({ step: 'picker', mode: 'change', id: editing.id })
          }
          onBack={() => {
            if (drawer.step === 'picker' && drawer.mode === 'change') {
              setDrawer({ step: 'edit', id: drawer.id });
            }
          }}
          onClose={() => setDrawer(null)}
        />
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('tenant.listBuilder.widgets.deleteTitle')}
        message={
          pendingDelete
            ? t('tenant.listBuilder.widgets.deleteMessage', {
                name: pendingDelete.label || t('tenant.listBuilder.widgets.unnamed'),
              })
            : ''
        }
        confirmLabel={t('common.delete')}
        onConfirm={() => {
          if (pendingDelete) removeWidget(pendingDelete.id);
          setPendingDelete(null);
        }}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Karta na liście (drag handle + etykieta + typ + akcent + edytuj/usuń)
// ────────────────────────────────────────────────────────────────────────────

function SortableWidgetCard({
  widget,
  onEdit,
  onDelete,
}: {
  widget: WidgetConfig;
  onEdit: () => void;
  onDelete: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: widget.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 rounded-lg border border-border-secondary bg-bg-elevated p-2.5"
      data-testid={`list-widgets-row-${widget.id}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={t('tenant.listBuilder.widgets.dragAria')}
        title={t('tenant.listBuilder.widgets.dragAria')}
        className="cursor-grab touch-none text-text-muted hover:text-text-primary active:cursor-grabbing"
        data-testid={`list-widgets-drag-${widget.id}`}
      >
        <GripVertical size={16} aria-hidden="true" />
      </button>

      <span
        className="h-4 w-1.5 flex-shrink-0 rounded-full"
        style={{ backgroundColor: widget.color || DEFAULT_COLOR }}
        aria-hidden="true"
      />

      <span className="min-w-0 flex-1 truncate text-sm text-text-primary">
        {widget.label || (
          <span className="text-text-muted">{t('tenant.listBuilder.widgets.unnamed')}</span>
        )}
      </span>

      <span className="flex-shrink-0 text-xs text-text-muted">
        {t(`tenant.listBuilder.widgets.types.${widget.type ?? 'counter'}`)}
      </span>

      <button
        type="button"
        onClick={onEdit}
        aria-label={t('tenant.listBuilder.widgets.editAria')}
        title={t('tenant.listBuilder.widgets.editAria')}
        className="rounded p-1.5 text-text-muted hover:text-text-primary"
        data-testid={`list-widgets-edit-${widget.id}`}
      >
        <Pencil size={15} aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={onDelete}
        aria-label={t('common.delete')}
        title={t('common.delete')}
        className="rounded p-1.5 text-error-500 hover:text-error-600"
        data-testid={`list-widgets-delete-${widget.id}`}
      >
        <Trash2 size={15} aria-hidden="true" />
      </button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Szuflada edycji — jeden panel, krok wyboru typu LUB formularz karty
// ────────────────────────────────────────────────────────────────────────────

function WidgetDrawer({
  state,
  editing,
  fieldOptions,
  defaultStyle,
  onPick,
  onUpdate,
  onChangeType,
  onBack,
  onClose,
}: {
  state: DrawerState;
  editing: WidgetConfig | null;
  fieldOptions: FieldOption[];
  defaultStyle: WidgetStyle;
  onPick: (type: WidgetType) => void;
  onUpdate: (patch: Partial<WidgetConfig>) => void;
  onChangeType: () => void;
  onBack: () => void;
  onClose: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const isPicker = state.step === 'picker';

  const title = isPicker
    ? t('tenant.listBuilder.widgets.pickerTitle')
    : editing && editing.label.trim()
      ? t('tenant.listBuilder.widgets.editTitle')
      : t('tenant.listBuilder.widgets.addTitle');

  // Stopka „Gotowe" tylko na kroku formularza — wybór typu sam przechodzi dalej.
  const footer =
    !isPicker && editing ? (
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg bg-accent-600 px-4 py-1.5 text-sm text-white hover:bg-accent-700"
        data-testid="list-widgets-drawer-done"
      >
        {t('tenant.listBuilder.widgets.done')}
      </button>
    ) : undefined;

  return (
    <Drawer open onClose={onClose} title={title} footer={footer} width="half">
      {isPicker ? (
        <div className="space-y-5">
          {state.mode === 'change' && (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center gap-1 text-xs text-accent-600 hover:text-accent-700"
              data-testid="list-widgets-picker-back"
            >
              <ArrowLeft size={13} aria-hidden="true" />
              {t('tenant.listBuilder.widgets.pickerBack')}
            </button>
          )}
          <WidgetTypeGrid onPick={onPick} />
        </div>
      ) : editing ? (
        <WidgetEditForm
          widget={editing}
          fieldOptions={fieldOptions}
          defaultStyle={defaultStyle}
          onUpdate={onUpdate}
          onChangeType={onChangeType}
        />
      ) : null}
    </Drawer>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Siatka wyboru typu widgetu (kategorie, „Wkrótce") — krok szuflady
// ────────────────────────────────────────────────────────────────────────────

function WidgetTypeGrid({ onPick }: { onPick: (type: WidgetType) => void }): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="space-y-5" data-testid="list-widgets-picker">
      {WIDGET_CATEGORIES.map((cat) => (
        <div key={cat.key} className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
            {t(`tenant.listBuilder.widgets.categories.${cat.key}`)}
          </h4>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {cat.types.map((ty) => {
              const available = AVAILABLE_TYPES.has(ty);
              return (
                <button
                  key={ty}
                  type="button"
                  disabled={!available}
                  onClick={() => available && onPick(ty)}
                  title={available ? undefined : t('tenant.listBuilder.widgets.soonHint')}
                  className={[
                    'flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm',
                    available
                      ? 'border-border-secondary text-text-primary hover:border-accent-500 hover:bg-bg-secondary'
                      : 'cursor-not-allowed border-border-secondary/60 text-text-muted',
                  ].join(' ')}
                  data-testid={`list-widgets-picker-${ty}`}
                >
                  <span className="truncate">{t(`tenant.listBuilder.widgets.types.${ty}`)}</span>
                  {!available && (
                    <span className="flex-shrink-0 rounded-full bg-bg-tertiary px-1.5 py-0.5 text-[10px] font-medium text-text-tertiary">
                      {t('tenant.listBuilder.widgets.soon')}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Pole wartości dla operatora „jest jednym z" (in) — lista po przecinku
// ────────────────────────────────────────────────────────────────────────────

/**
 * Wpisywanie wielu wartości dla operatora `in`. Wartość zapisywana jest jako
 * tablica stringów (backend filtruje przez `IN (...)`), ale lokalnie trzymamy
 * surowy tekst — inaczej natychmiastowa normalizacja zjadałaby przecinki i
 * spacje podczas pisania (np. nie dałoby się wpisać „nowy, w toku").
 */
function InValueInput({
  value,
  onChange,
  placeholder,
  className,
}: {
  value: unknown;
  onChange: (next: string[]) => void;
  placeholder: string;
  className: string;
}): React.ReactElement {
  const [text, setText] = useState(() => (Array.isArray(value) ? value.join(', ') : ''));

  return (
    <input
      type="text"
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        onChange(parseInList(e.target.value));
      }}
      placeholder={placeholder}
      className={className}
      data-testid="list-widgets-modal-in-values"
    />
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Formularz pojedynczej karty — krok szuflady
// ────────────────────────────────────────────────────────────────────────────

function WidgetEditForm({
  widget,
  fieldOptions,
  defaultStyle,
  onUpdate,
  onChangeType,
}: {
  widget: WidgetConfig;
  fieldOptions: FieldOption[];
  defaultStyle: WidgetStyle;
  onUpdate: (patch: Partial<WidgetConfig>) => void;
  onChangeType: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const isAggregate = widget.type === 'aggregate';
  const conditions = widget.filters ?? [];
  const useCustomStyle = widget.style != null;

  const addCondition = () =>
    onUpdate({
      filters: [...conditions, { field: fieldOptions[0]?.id ?? '', operator: 'eq', value: '' }],
    });

  const updateCondition = (index: number, patch: Partial<FilterConfig>) =>
    onUpdate({ filters: conditions.map((c, i) => (i === index ? { ...c, ...patch } : c)) });

  const removeCondition = (index: number) =>
    onUpdate({ filters: conditions.filter((_, i) => i !== index) });

  const inputClass =
    'rounded border border-border-secondary bg-bg-elevated px-2 py-1 text-sm text-text-primary';

  return (
    <div className="space-y-5" data-testid="list-widgets-edit-form">
      {/* Sekcja: Dane */}
      <section className="space-y-3">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
          {t('tenant.listBuilder.widgets.sectionData')}
        </h4>

        <input
          type="text"
          value={widget.label}
          onChange={(e) => onUpdate({ label: e.target.value })}
          placeholder={t('tenant.listBuilder.widgets.labelPlaceholder')}
          maxLength={120}
          className={`w-full ${inputClass}`}
          data-testid="list-widgets-modal-label"
        />

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-text-secondary">
              {t('tenant.listBuilder.widgets.typeLabel')}
            </span>
            <span className="rounded border border-border-secondary px-2 py-1 text-sm text-text-primary">
              {t(`tenant.listBuilder.widgets.types.${widget.type ?? 'counter'}`)}
            </span>
            <button
              type="button"
              onClick={onChangeType}
              className="text-xs text-accent-600 hover:text-accent-700"
              data-testid="list-widgets-modal-change-type"
            >
              {t('tenant.listBuilder.widgets.changeType')}
            </button>
          </div>

          <label className="flex items-center gap-2 text-xs text-text-secondary">
            {t('tenant.listBuilder.widgets.color')}
            <input
              type="color"
              value={widget.color || DEFAULT_COLOR}
              onChange={(e) => onUpdate({ color: e.target.value })}
              aria-label={t('tenant.listBuilder.widgets.color')}
              className="h-8 w-10 cursor-pointer rounded border border-border-secondary bg-bg-elevated"
              data-testid="list-widgets-modal-color"
            />
          </label>
        </div>

        {/* Agregacja — funkcja + pole */}
        {isAggregate && (
          <div className="flex flex-wrap items-center gap-2">
            <Dropdown
              options={AGG_FUNCTIONS.map((fn) => ({
                value: fn,
                label: t(`tenant.listBuilder.widgets.aggFns.${fn}`),
              }))}
              value={widget.aggregateFunction ?? 'sum'}
              onChange={(val) => onUpdate({ aggregateFunction: val as AggregateFunction })}
              className="w-auto px-2 py-1"
              data-testid="list-widgets-modal-aggfn"
            />
            <Dropdown
              options={[
                { value: '', label: t('tenant.listBuilder.widgets.selectField') },
                ...fieldOptions.map((f) => ({ value: f.id, label: f.label })),
              ]}
              value={widget.aggregateField ?? ''}
              onChange={(val) => onUpdate({ aggregateField: val || undefined })}
              className="w-auto px-2 py-1"
              data-testid="list-widgets-modal-aggfield"
            />
          </div>
        )}

        {/* Warunki filtra (AND) */}
        <div className="space-y-1.5">
          {conditions.map((cond, index) => (
            <div key={index} className="flex items-center gap-2">
              <Dropdown
                options={fieldOptions.map((f) => ({ value: f.id, label: f.label }))}
                value={cond.field}
                onChange={(val) => updateCondition(index, { field: val })}
                className="w-auto px-2 py-1"
              />
              <Dropdown
                options={OPERATORS.map((op) => ({
                  value: op,
                  label: t(`tenant.listBuilder.qf.ops.${op}`),
                }))}
                value={cond.operator}
                onChange={(val) => {
                  const nextOp = val as FilterOperator;
                  const patch: Partial<FilterConfig> = { operator: nextOp };
                  // Konwersja wartości na granicy operatora „jest jednym z":
                  // wchodząc w `in` zamień string na tablicę, wychodząc — sklej z powrotem.
                  if (nextOp === 'in' && !Array.isArray(cond.value)) {
                    patch.value = cond.value == null ? [] : parseInList(String(cond.value));
                  } else if (nextOp !== 'in' && Array.isArray(cond.value)) {
                    patch.value = cond.value.join(', ');
                  }
                  updateCondition(index, patch);
                }}
                className="w-auto px-2 py-1"
              />
              {cond.operator === 'in' ? (
                <InValueInput
                  value={cond.value}
                  onChange={(next) => updateCondition(index, { value: next })}
                  placeholder={t('tenant.listBuilder.qf.valuePlaceholderIn')}
                  className={`flex-1 ${inputClass}`}
                />
              ) : (
                <input
                  type="text"
                  value={NO_VALUE_OPS.has(cond.operator) ? '' : String(cond.value ?? '')}
                  disabled={NO_VALUE_OPS.has(cond.operator)}
                  onChange={(e) => updateCondition(index, { value: e.target.value })}
                  placeholder={t('tenant.listBuilder.qf.valuePlaceholder')}
                  className={`flex-1 ${inputClass} disabled:opacity-40`}
                />
              )}
              <button
                type="button"
                onClick={() => removeCondition(index)}
                aria-label={t('tenant.listBuilder.qf.removeCondition')}
                title={t('tenant.listBuilder.qf.removeCondition')}
                className="rounded p-1 text-text-muted hover:text-red-600"
              >
                <Trash2 size={12} aria-hidden="true" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addCondition}
            className="inline-flex items-center gap-1 text-xs text-accent-600 hover:text-accent-700"
            data-testid="list-widgets-modal-add-condition"
          >
            <Plus size={12} aria-hidden="true" />
            {t('tenant.listBuilder.widgets.addCondition')}
          </button>
        </div>
      </section>

      {/* Sekcja: Wygląd (per-widget, nadpisuje domyślny) */}
      <section className="space-y-3 border-t border-border-secondary pt-4">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
          {t('tenant.listBuilder.widgets.sectionStyle')}
        </h4>

        <label
          className="flex items-center gap-2 text-sm text-text-primary"
          data-testid="list-widgets-modal-use-custom"
        >
          <input
            type="checkbox"
            checked={useCustomStyle}
            onChange={(e) =>
              onUpdate({ style: e.target.checked ? { ...defaultStyle } : undefined })
            }
          />
          <span>
            {t('tenant.listBuilder.widgets.style.useCustom')}
            <span className="block text-xs text-text-muted">
              {t('tenant.listBuilder.widgets.style.useCustomHint')}
            </span>
          </span>
        </label>

        {useCustomStyle && widget.style && (
          <>
            <WidgetStyleEditor
              value={widget.style}
              onChange={(next) => onUpdate({ style: next })}
              testId="list-widgets-modal"
            />
            <WidgetStylePreview style={widget.style} />
          </>
        )}
      </section>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Edytor stylu karty (współdzielony: domyślny + per-widget)
// ────────────────────────────────────────────────────────────────────────────

function ColorField({
  label,
  value,
  onChange,
  testId,
}: {
  label: string;
  value: string | undefined;
  onChange: (next: string | undefined) => void;
  testId: string;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="space-y-1">
      <span className="block text-xs font-medium text-text-secondary">{label}</span>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value ?? '#ffffff'}
          onChange={(e) => onChange(e.target.value)}
          className="h-7 w-9 cursor-pointer rounded border border-border-secondary bg-transparent p-0.5"
          data-testid={`${testId}-picker`}
          aria-label={label}
        />
        <span className="font-mono text-xs text-text-muted">
          {value ?? t('tenant.listBuilder.widgets.style.themeDefault')}
        </span>
        {value != null && (
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className="text-xs text-text-tertiary hover:text-text-primary"
            data-testid={`${testId}-reset`}
          >
            {t('tenant.listBuilder.widgets.style.reset')}
          </button>
        )}
      </div>
    </div>
  );
}

function WidgetStyleEditor({
  value,
  onChange,
  testId,
}: {
  value: WidgetStyle;
  onChange: (next: WidgetStyle) => void;
  testId: string;
}): React.ReactElement {
  const { t } = useTranslation();
  const set = <K extends keyof WidgetStyle>(key: K, v: WidgetStyle[K]) =>
    onChange({ ...value, [key]: v });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <ColorField
          label={t('tenant.listBuilder.widgets.style.bg')}
          value={value.backgroundColor}
          onChange={(v) => set('backgroundColor', v)}
          testId={`${testId}-bg`}
        />
        <ColorField
          label={t('tenant.listBuilder.widgets.style.text')}
          value={value.textColor}
          onChange={(v) => set('textColor', v)}
          testId={`${testId}-text`}
        />
        <ColorField
          label={t('tenant.listBuilder.widgets.style.header')}
          value={value.headerColor}
          onChange={(v) => set('headerColor', v)}
          testId={`${testId}-header`}
        />
        <div className="space-y-1">
          <label className="block text-xs font-medium text-text-secondary">
            {t('tenant.listBuilder.widgets.style.rounding')}
          </label>
          <Dropdown
            options={[
              { value: '', label: t('tenant.listBuilder.widgets.style.themeDefault') },
              ...ROUNDING_OPTIONS.map((r) => ({
                value: r,
                label: t(`tenant.listBuilder.widgets.style.roundingScale.${r}`),
              })),
            ]}
            value={value.rounding ?? ''}
            onChange={(val) => set('rounding', val ? (val as RoundingScale) : undefined)}
            className="w-40 px-2 py-1"
            data-testid={`${testId}-round`}
          />
        </div>
        <div className="space-y-1">
          <label className="block text-xs font-medium text-text-secondary">
            {t('tenant.listBuilder.widgets.style.shadow')}
          </label>
          <Dropdown
            options={[
              { value: '', label: t('tenant.listBuilder.widgets.style.themeDefault') },
              ...SHADOW_OPTIONS.map((s) => ({
                value: s,
                label: t(`tenant.listBuilder.widgets.style.shadowScale.${s}`),
              })),
            ]}
            value={value.shadow ?? ''}
            onChange={(val) => set('shadow', val ? (val as ShadowScale) : undefined)}
            className="w-40 px-2 py-1"
            data-testid={`${testId}-shadow`}
          />
        </div>
      </div>

      {/* Gradient tła */}
      <div className="space-y-2">
        <label
          className="flex items-center gap-2 text-sm text-text-primary"
          data-testid={`${testId}-gradient-toggle`}
        >
          <input
            type="checkbox"
            checked={!!value.backgroundGradient}
            onChange={(e) =>
              set(
                'backgroundGradient',
                e.target.checked ? { from: '#6366f1', to: '#8b5cf6' } : null,
              )
            }
          />
          <span>
            {t('tenant.listBuilder.widgets.style.gradient')}
            <span className="block text-xs text-text-muted">
              {t('tenant.listBuilder.widgets.style.gradientHint')}
            </span>
          </span>
        </label>
        {value.backgroundGradient && (
          <div className="grid grid-cols-1 gap-4 pl-6 sm:grid-cols-2">
            <ColorField
              label={t('tenant.listBuilder.widgets.style.gradientFrom')}
              value={value.backgroundGradient.from}
              onChange={(v) =>
                set('backgroundGradient', {
                  from: v ?? '#6366f1',
                  to: value.backgroundGradient?.to ?? '#8b5cf6',
                })
              }
              testId={`${testId}-gradient-from`}
            />
            <ColorField
              label={t('tenant.listBuilder.widgets.style.gradientTo')}
              value={value.backgroundGradient.to}
              onChange={(v) =>
                set('backgroundGradient', {
                  from: value.backgroundGradient?.from ?? '#6366f1',
                  to: v ?? '#8b5cf6',
                })
              }
              testId={`${testId}-gradient-to`}
            />
          </div>
        )}
      </div>

      {/* Efekt szkła */}
      <label
        className="flex items-center gap-2 text-sm text-text-primary"
        data-testid={`${testId}-glass`}
      >
        <input
          type="checkbox"
          checked={!!value.glassEffect}
          onChange={(e) => set('glassEffect', e.target.checked)}
        />
        <span>
          {t('tenant.listBuilder.widgets.style.glass')}
          <span className="block text-xs text-text-muted">
            {t('tenant.listBuilder.widgets.style.glassHint')}
          </span>
        </span>
      </label>
    </div>
  );
}

function WidgetStylePreview({ style }: { style: WidgetStyle }): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg bg-bg-secondary p-4" data-testid="list-widgets-style-preview-wrap">
      <div
        className="w-48 p-4"
        style={cardStyleFrom(style)}
        data-testid="list-widgets-style-preview"
      >
        <p className="text-xs" style={{ color: style.headerColor }}>
          {t('tenant.listBuilder.page.appearance.previewWidgetHeader')}
        </p>
        <p className="mt-1 text-2xl font-semibold">128</p>
      </div>
    </div>
  );
}
